import axios from "axios";
import React, { useEffect, useState } from "react";
import { Line } from "react-chartjs-2";
function getDateRange(dateString) {
  // Parse the given date string
  const date = new Date(dateString);

  // Calculate the start date: date - 5 days - 1 year
  const startDate = new Date(date);
  startDate.setDate(startDate.getDate() - 5);
  startDate.setFullYear(startDate.getFullYear() - 1);

  // Calculate the end date: date - 5 days
  const endDate = new Date(date);
  endDate.setDate(endDate.getDate() - 5);

  // Format dates as YYYY-MM-DD strings
  const formatDate = (d) => {
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  return { startDate: formatDate(startDate), endDate: formatDate(endDate) };
}

function normalizeCoordinates(input) {
  input = input.trim();

  // Already decimal coordinates
  const decimalRegex = /^\s*-?\d+(\.\d+)?\s*,\s*-?\d+(\.\d+)?\s*$/;

  if (decimalRegex.test(input)) {
    const cleanedInput = input.replace(/\s+/g, "");
    const [lat, lon] = cleanedInput.split(",").map(Number);

    return {
      latitude: lat.toFixed(7),
      longitude: lon.toFixed(7),
    };
  }

  // DMS coordinates
  const dmsRegex =
    /(\d+)°(\d+)'([\d.]+)"([NSEW])\s+(\d+)°(\d+)'([\d.]+)"([NSEW])/i;

  const match = input.match(dmsRegex);

  if (!match) {
    return null; // Invalid format
  }

  const [, latDeg, latMin, latSec, latDir, lonDeg, lonMin, lonSec, lonDir] =
    match;

  let lat = Number(latDeg) + Number(latMin) / 60 + Number(latSec) / 3600;

  let lon = Number(lonDeg) + Number(lonMin) / 60 + Number(lonSec) / 3600;

  if (latDir.toUpperCase() === "S") lat *= -1;
  if (lonDir.toUpperCase() === "W") lon *= -1;

  return { latitude: lat.toFixed(7), longitude: lon.toFixed(7) };
}

const CheckTemprature = ({ SIData }) => {
  const [data, setData] = useState(null);
  const [latitude, setLatitude] = useState(null);
  const [longitude, setLongitude] = useState(null);
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [show, setShow] = useState(false);

  useEffect(() => {
    if (SIData?.land?.googleMapsLocationLink) {
      let data = normalizeCoordinates(SIData?.land?.googleMapsLocationLink);

      setLatitude(data?.latitude);
      setLongitude(data?.longitude);
    }
    if (SIData?.createdAt) {
      let dates = getDateRange(SIData?.createdAt);
      setStartDate(dates?.startDate);
      setEndDate(dates?.endDate);
    }
  }, [SIData?.land?.googleMapsLocationLink]);
  useEffect(() => {
    if (latitude && longitude && startDate && endDate) {
      let apiUrl = `https://archive-api.open-meteo.com/v1/archive?latitude=${latitude}&longitude=${longitude}&start_date=${startDate}&end_date=${endDate}&daily=temperature_2m_max,temperature_2m_min&timezone=auto`;
      axios
        .get(apiUrl)
        .then((res) => {
          const daily = res.data?.daily;

          // Step 1: Group daily averages and track monthly highest temp
          const monthlyData = {};
          let maxCount = 0;
          for (let i = 0; i < daily?.time.length; i++) {
            const date = new Date(daily?.time[i]);
            const month = date.toLocaleString("default", { month: "short" }); // e.g. "Jan"

            const avgTemp = daily?.temperature_2m_max[i];

            if (!monthlyData[month]) {
              monthlyData[month] = { sum: 0, count: 0, highestTemp: -Infinity };
            }

            monthlyData[month].sum += avgTemp;
            monthlyData[month].count++;

            // Track highest temperature for that month
            if (avgTemp > monthlyData[month].highestTemp) {
              monthlyData[month].highestTemp = avgTemp;
            }
          }

          // Step 2: Convert to clean array and calculate monthly averages
          const monthlyAverages = Object.entries(monthlyData).map(
            ([month, { sum, count, highestTemp }]) => {
              if (highestTemp >= 40) {
                maxCount++;
              }
              return {
                month,
                avgTemp: +(sum / count).toFixed(1),
                highestTemp: +highestTemp.toFixed(1),
              };
            }
          );

          // Optional: sort months in calendar order
          const monthOrder = [
            "Jan",
            "Feb",
            "Mar",
            "Apr",
            "May",
            "Jun",
            "Jul",
            "Aug",
            "Sep",
            "Oct",
            "Nov",
            "Dec",
          ];

          monthlyAverages?.sort(
            (a, b) =>
              monthOrder?.indexOf(a.month) - monthOrder?.indexOf(b.month)
          );
          setData(monthlyAverages);
          if (maxCount > 2) {
            setShow(true);
          }
        })
        .catch((err) => console.error(err));
    }
  }, [latitude, longitude, startDate, endDate]);

  const chartData = {
    labels: data?.map((item) => item?.month),
    datasets: [
      {
        label: "Max. Temperature (°C)",
        data: data?.map((item) => item?.highestTemp),
        borderColor: "#ef4444",
        backgroundColor: "rgba(239, 68, 68, 0.2)",
        borderWidth: 1,
        pointRadius: 5,
        tension: 0.4,
      },
      {
        label: "Average (°C)",
        data: data?.map((item) => item?.avgTemp),
        borderColor: "#3b82f6",
        backgroundColor: "rgba(59, 130, 246, 0.2)",
        borderWidth: 1,
        pointRadius: 5,
        tension: 0.4,
      },
    ],
  };

  const options = {
    responsive: true,
    plugins: {
      legend: {
        position: "bottom",
        labels: { color: "#374151", font: { size: 13 } },
      },
      title: {
        display: true,
        text: "📈 Temperature Trend",
        color: "black",
        font: { size: 18 },
      },
    },
    scales: {
      x: {
        ticks: { color: "#374151" },
        grid: { color: "#e5e7eb" },
      },
      y: {
        ticks: { color: "#374151" },
        grid: { color: "#e5e7eb" },
      },
    },
  };
  let serial = 1;
  return show ? (
    <>
      <div
        style={{
          border: "3px solid red",
          padding: "0 10px 0 10px",
          backgroundColor: "#ffefea",
        }}
      >
        <h3 className="text-uppercase text-red">
          Suggested System: Fan & Pad System
        </h3>
        <h3 className="mt-0">
          NVPH or Nethouse system is not suitable under high temperature
          conditions.
        </h3>
        <table style={{ width: "100%" }}>
          <thead>
            <tr>
              <th style={{ width: "5%" }}>Sr.</th>
              <th>Month</th>
              <th>Max. Temperature (°C)</th>
              <th>Average (°C)</th>
            </tr>
          </thead>
          <tbody>
            {data?.map((item, idx) => (
              <tr key={idx}>
                <td className={item?.highestTemp >= 40 ? "text-red" : ""}>
                  {serial++}
                </td>
                <td className={item?.highestTemp >= 40 ? "text-red" : ""}>
                  {item?.month}
                </td>
                <td className={item?.highestTemp >= 40 ? "text-red" : ""}>
                  {item?.highestTemp} °C
                </td>
                <td className={item?.highestTemp >= 40 ? "text-red" : ""}>
                  {item?.avgTemp} °C
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <div>
          <div>
            <div>
              <h3>Max Temperature (°C)</h3>
              <div
                style={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                }}
              >
                <div style={{ height: "350px", width: "95%" }}>
                  <Line
                    data={chartData}
                    options={{
                      ...options,
                      responsive: true,
                      maintainAspectRatio: false, // 👈 allow custom height
                    }}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div
        className="html2pdf__page-break"
        style={{
          pageBreakAfter: "always",
          display: "block",
          clear: "both",
        }}
      ></div>
    </>
  ) : (
    ""
  );
};

export default CheckTemprature;
